import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Image as ImageIcon, Camera, X, Loader2, RefreshCw, Layers, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { ROOF_TYPES, ROOF_TYPE_TONE, tLabel, systemName } from "@/lib/roofTypes";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const assetSrc = (u) => (u && u.startsWith("/api/") ? BACKEND + u : u);
const inp = "border-2 border-slate-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-navy";
const money = (c) => `$${Math.round((c || 0) / 100).toLocaleString()}`;
const newId = () => Math.random().toString(36).slice(2, 10);

export const newSystem = (extra = {}) => ({
  id: newId(), roof_type: "SHINGLE", label: "", product: "", color: "", squares: "", price: "", price_auto: true,
  template_ids: [], scope_items: [], before_photos: [], after_photos: [], ...extra,
});

export const systemFromSaved = (s) => ({ ...newSystem(), ...s, squares: s.squares ? String(s.squares) : "", price: s.price_cents ? String(s.price_cents / 100) : "" });

export const systemToPayload = (s) => ({
  ...s, squares: Number(s.squares || 0), price_cents: Math.round(Number(s.price || 0) * 100),
  scope_items: s.scope_items.filter((i) => i.title),
});

export const systemCents = (s) => Math.round(Number(s.price || 0) * 100);

const uploadPhoto = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/proposal-asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

function usePriceBook(sys, onChange) {
  const [busy, setBusy] = useState(false);
  const lastKey = useRef(sys.price ? `${sys.roof_type}|${sys.squares}` : "");
  const recalc = async (force) => {
    const sq = Number(sys.squares || 0);
    if (!sq || (!sys.price_auto && !force)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/proposals/system-price", { roof_type: sys.roof_type, squares: sq });
      onChange((s) => ({ ...s, price: String(Math.round(data.price_cents / 100)), price_auto: true, product: s.product || data.product }));
    } catch { toast.error("Could not price from PriceBook"); } finally { setBusy(false); }
  };
  useEffect(() => {
    const key = `${sys.roof_type}|${sys.squares}`;
    if (key === lastKey.current) return undefined;
    const t = setTimeout(() => { lastKey.current = key; recalc(false); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sys.roof_type, sys.squares]);
  return [busy, () => recalc(true)];
}

function TemplatePicker({ sys, templates, onChange }) {
  const toggle = (t) => {
    if (sys.template_ids.includes(t.id)) {
      onChange((s) => ({ ...s, template_ids: s.template_ids.filter((x) => x !== t.id), scope_items: s.scope_items.filter((i) => i.template_id !== t.id) }));
    } else {
      const add = (t.items || []).map((i) => ({ title: i.title, description: i.description || "", image_url: i.image_url || "", template_id: t.id }));
      onChange((s) => ({ ...s, template_ids: [...s.template_ids, t.id], scope_items: [...s.scope_items, ...add] }));
    }
  };
  if (!templates.length) return <div className="text-xs text-slate-400">No scope templates yet — create them under Scope Templates.</div>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`sys-templates-${sys.id}`}>
      {templates.map((t) => {
        const on = sys.template_ids.includes(t.id);
        return (
          <button key={t.id} type="button" onClick={() => toggle(t)} data-testid={`sys-tpl-${sys.id}-${t.id}`}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border-2 transition-colors ${on ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-600 hover:border-navy"}`}>
            {t.name} <span className="opacity-60">({t.items?.length || 0})</span>
          </button>
        );
      })}
    </div>
  );
}

function ScopeList({ sys, onChange }) {
  const set = (i, k, v) => onChange((s) => ({ ...s, scope_items: s.scope_items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const rm = (i) => onChange((s) => ({ ...s, scope_items: s.scope_items.filter((_, idx) => idx !== i) }));
  const move = (i, d) => onChange((s) => {
    const a = [...s.scope_items]; const j = i + d;
    if (j < 0 || j >= a.length) return s;
    [a[i], a[j]] = [a[j], a[i]]; return { ...s, scope_items: a };
  });
  return (
    <div className="space-y-1.5">
      {sys.scope_items.map((it, i) => (
        <div key={i} className="flex gap-2 items-start border border-slate-100 rounded-lg p-1.5" data-testid={`sys-scope-${sys.id}-${i}`}>
          <div className="w-10 h-10 rounded border border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">{it.image_url ? <img src={assetSrc(it.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}</div>
          <div className="flex-1 space-y-1 min-w-0">
            <input value={it.title} onChange={(e) => set(i, "title", e.target.value)} placeholder="Step title" className={`${inp} w-full`} data-testid={`sys-scope-title-${sys.id}-${i}`} />
            <input value={it.description} onChange={(e) => set(i, "description", e.target.value)} placeholder="Description" className={`${inp} w-full text-xs`} />
          </div>
          <div className="flex flex-col"><button type="button" onClick={() => move(i, -1)} className="text-slate-400 hover:text-navy"><ChevronUp className="w-4 h-4" /></button><button type="button" onClick={() => move(i, 1)} className="text-slate-400 hover:text-navy"><ChevronDown className="w-4 h-4" /></button></div>
          <button type="button" onClick={() => rm(i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid={`sys-add-scope-${sys.id}`} onClick={() => onChange((s) => ({ ...s, scope_items: [...s.scope_items, { title: "", description: "", image_url: "" }] }))} className="text-xs text-navy font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add step</button>
    </div>
  );
}

function PhotoStrip({ sys, field, label, captions, onChange, library }) {
  const photos = sys[field] || [];
  const add = (ph) => onChange((s) => ({ ...s, [field]: [...(s[field] || []), ph] }));
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) return toast.error("Under 25 MB");
    try { add({ url: await uploadPhoto(f), caption: "" }); } catch { toast.error("Upload failed"); }
  };
  return (
    <div data-testid={`sys-${field}-${sys.id}`}>
      <div className="text-xs font-semibold uppercase text-slate-500 mb-1 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> {label} ({photos.length})</div>
      <div className="space-y-1.5">
        {photos.map((ph, i) => (
          <div key={i} className="flex gap-2 items-center">
            <img src={assetSrc(ph.url)} alt="" className="w-12 h-12 rounded object-cover border border-slate-200 shrink-0" />
            {captions && <input value={ph.caption || ""} onChange={(e) => onChange((s) => ({ ...s, [field]: s[field].map((x, idx) => (idx === i ? { ...x, caption: e.target.value } : x)) }))} placeholder="Describe the problem" className={`${inp} flex-1 text-xs min-w-0`} />}
            <button type="button" onClick={() => onChange((s) => ({ ...s, [field]: s[field].filter((_, idx) => idx !== i) }))} className="text-slate-400 hover:text-brandred"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <label className="text-xs text-navy font-semibold flex items-center gap-1 mt-1.5 cursor-pointer w-fit">
        <input type="file" accept="image/*" className="hidden" data-testid={`sys-upload-${field}-${sys.id}`} onChange={pick} /><Plus className="w-3.5 h-3.5" /> Upload photo
      </label>
      {library.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {library.map((ph, i) => <button type="button" key={i} onClick={() => add({ url: ph.url, caption: ph.caption || "" })} className="w-10 h-10 rounded overflow-hidden border-2 border-slate-200 hover:border-navy"><img src={assetSrc(ph.url)} alt="" className="w-full h-full object-cover" /></button>)}
        </div>
      )}
    </div>
  );
}

function SystemCard({ sys, idx, templates, library, onChange, onRemove }) {
  const [busy, recalc] = usePriceBook(sys, onChange);
  const set = (k, v) => onChange((s) => ({ ...s, [k]: v }));
  return (
    <div className="border-2 border-slate-200 rounded-xl p-3 space-y-3" data-testid={`system-card-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`${ROOF_TYPE_TONE[sys.roof_type]} text-white text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full`}>{systemName(sys, idx)}</span>
        <button type="button" onClick={onRemove} data-testid={`system-remove-${idx}`} className="text-slate-400 hover:text-brandred flex items-center gap-1 text-xs"><Trash2 className="w-4 h-4" /> Remove</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <select value={sys.roof_type} onChange={(e) => set("roof_type", e.target.value)} className={inp} data-testid={`system-type-${idx}`}>
          {ROOF_TYPES.map((t) => <option key={t} value={t}>{tLabel(t)}</option>)}
        </select>
        <input value={sys.label} onChange={(e) => set("label", e.target.value)} placeholder="Area name (e.g. Porch)" className={inp} data-testid={`system-label-${idx}`} />
        <input type="number" min="0" step="0.1" value={sys.squares} onChange={(e) => set("squares", e.target.value)} placeholder="Squares (incl. waste)" className={`${inp} font-num`} data-testid={`system-squares-${idx}`} />
        <div className="flex items-center gap-1">
          <span className="text-slate-400 text-sm">$</span>
          <input type="number" min="0" value={sys.price} onChange={(e) => onChange((s) => ({ ...s, price: e.target.value, price_auto: false }))} placeholder="Price" className={`${inp} font-num w-full`} data-testid={`system-price-${idx}`} />
          <button type="button" title="Recalculate from PriceBook" onClick={recalc} className="text-navy" data-testid={`system-recalc-${idx}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
        </div>
        <input value={sys.product} onChange={(e) => set("product", e.target.value)} placeholder="Product / material" className={`${inp} col-span-2`} data-testid={`system-product-${idx}`} />
        <input value={sys.color} onChange={(e) => set("color", e.target.value)} placeholder="Color (optional)" className={`${inp} col-span-2`} data-testid={`system-color-${idx}`} />
      </div>
      <div className="text-[11px] text-slate-400" data-testid={`system-price-source-${idx}`}>{sys.price_auto ? "Price calculated from PriceBook — squares are used as entered (no extra waste added)." : "Price entered manually. Click the refresh icon to use the PriceBook price."}</div>
      <div>
        <div className="text-xs font-semibold uppercase text-slate-500 mb-1 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Scope templates for this system</div>
        <TemplatePicker sys={sys} templates={templates} onChange={onChange} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Scope of Work ({sys.scope_items.length})</div>
        <ScopeList sys={sys} onChange={onChange} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <PhotoStrip sys={sys} field="before_photos" label="Before photos" captions onChange={onChange} library={library} />
        <PhotoStrip sys={sys} field="after_photos" label="After photos" onChange={onChange} library={library} />
      </div>
    </div>
  );
}

export function ProposalSystemsEditor({ systems, setSystems, templates, library, onFirstAdd, defaultSquares }) {
  const update = (id) => (fn) => setSystems((a) => a.map((s) => (s.id === id ? fn(s) : s)));
  const sq = defaultSquares ? { squares: String(defaultSquares) } : {};
  const add = () => setSystems((a) => [...a, a.length === 0 && onFirstAdd ? { ...onFirstAdd(), ...sq } : newSystem(sq)]);
  const total = systems.reduce((n, s) => n + systemCents(s), 0);
  return (
    <div className="border-2 border-navy/20 rounded-xl p-3 mb-5" data-testid="systems-editor">
      <div className="font-semibold text-navy text-sm mb-1">Roof Systems ({systems.length})</div>
      <div className="text-[11px] uppercase text-slate-400 mb-3">Add one system per roof area — e.g. 30 sq Shingle + 5 sq Metal. Each gets its own scope, photos and price.{defaultSquares ? ` New systems start at ${defaultSquares} sq (measured + 15% waste) — change it for smaller areas.` : ""}</div>
      <div className="space-y-3">
        {systems.map((s, i) => <SystemCard key={s.id} sys={s} idx={i} templates={templates} library={library} onChange={update(s.id)} onRemove={() => setSystems((a) => a.filter((x) => x.id !== s.id))} />)}
      </div>
      <div className="flex items-center justify-between mt-3">
        <button type="button" data-testid="add-system" onClick={add} className="text-sm text-navy font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Add roof system</button>
        {systems.length > 0 && <div className="text-sm text-slate-500">Total Investment: <b className="font-num text-navy text-lg" data-testid="systems-total">{money(total)}</b></div>}
      </div>
    </div>
  );
}
